import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import Icon from './icons/Icon';
import ProjectCard from './ProjectCard';
import PlaybookPicker from './PlaybookPicker';
import {VoiceWave} from '@mia-platform-internal/chatbot-widget';
import useHandsFreePrompt from '../hooks/useHandsFreePrompt';
import usePlanFeatures, {planLabel} from '../hooks/usePlanFeatures';
import {offerVoiceHandoff} from '../utils/voiceHandoff';
import {appRequested, friction} from '../analytics/journey';
import {getProjectThumbnail} from '../utils/screenshotCapture';
import {listAdkSessions, listActiveAdkSessions, parseVfsSessionId} from '../services/AdkSessionsService';
import {listPlaybooks} from '../services/PlaybooksService';
import {readProjectMetadata, findProjectByConversationId} from '../utils/vfsClient';
import miaFlowAuth from '../auth/MiaFlowAuth';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import './Home.css';
import './HomeLanding.css';
import './HomeSimple.css';
import HomeSimple from './HomeSimple';
import { ProjectCardSkeleton, ListRowsSkeleton, ContinueSkeleton } from './Skeletons';
import { devLog } from '../utils/devLog';
import { formatRelativeTime } from '../utils/relativeTime';
import { projectHomeUrl } from '../utils/openProject';
import useProjectImportOffer from './sandbox/ProjectImportOffer';
import { lastActivityAt, lastActivityMs } from '../utils/projectRecency';
import useChatFeature from '../hooks/useChatFeature';

const MAX_RECENT_PROJECTS = 3;

// The simple view (three big choices, then one question) is what Home starts with; the switch is a per-user preference that is
// remembered, so someone who turned it off keeps the full home.
const SIMPLE_VIEW_KEY = 'flow.home.simple';
const readSimpleView = () => {
  try { return localStorage.getItem(SIMPLE_VIEW_KEY) !== 'false'; } catch (_error) { return true; }
};

// A headset: talk hands-free, drawn here in the button's own colour.
const HeadsetIcon = ({size = 16}) => (
  <svg aria-hidden="true" fill="none" height={size} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width={size}>
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <rect height="6" rx="2" width="4" x="3" y="14" />
    <rect height="6" rx="2" width="4" x="17" y="14" />
    <path d="M19 20c0 1.5-2 2-5 2" />
  </svg>
);
HeadsetIcon.propTypes = {size: PropTypes.number};

// Stable defaults: a `[]` written as a parameter default is a new array on every render, and the effect keyed on `projects`
// would then run on every render and set state again, forever.
const NO_PROJECTS = [];
const NO_TEMPLATES = [];

// An arrow drawn here, in the button's own colour: the shared icon came out invisible on the blue buttons.
const ArrowIcon = ({size = 18}) => (
  <svg aria-hidden="true" fill="none" height={size} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" viewBox="0 0 24 24" width={size}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
ArrowIcon.propTypes = {size: PropTypes.number};

/**
 * Common utility to activate code layout (canvas mode) with optional project context
 * @param {Object} options - Configuration options
 * @param {string} options.tenantId - Tenant ID (optional)
 * @param {string} options.projectId - Project ID (optional)
 * @param {Object} options.tenant - Full tenant object (optional)
 * @param {Object} options.project - Full project object (optional)
 * @param {Object} options.revision - Revision object (optional)
 */
const activateCodeLayout = ({tenantId, projectId, tenant, project, revision} = {}) => {
  try {
    // Always activate canvas mode
    localStorage.setItem('canvasMode', 'true');
    
    // If project context is provided, store it
    if ((tenant || tenantId) && (project || projectId)) {
      const projectContext = {
        tenant: tenant || {tenantId},
        project: project || {
          _id: projectId,
          projectId: projectId,
        },
        revision: revision || null
      };
      
      localStorage.setItem('canvasProjectContext', JSON.stringify(projectContext));
    }
    
    devLog('✅ Code layout activated', {tenantId, projectId});
  } catch (error) {
    console.error('Failed to activate code layout:', error);
  }
};

const Home = ({
  onStartChat,
  onSelectProject,
  onSelectTemplate,
  onNavigate,
  projects = NO_PROJECTS,
  templates = NO_TEMPLATES,
  user,
  selectedTenant,
  hasGitConnection = false,
  // Set by App.jsx (navigateWithHomeFade) for the brief window between the
  // omnibar submit resolving and the delayed navigate() actually firing —
  // drives a CSS opacity transition (Home.css) so the automatic Home→Chat
  // handoff reads as a fade instead of an instant cut.
  fadeOut = false,
  // Sandbox-first with Chat on (pages/SandboxHome.jsx): the person's sandbox, compact, under the prompt and chips.
  sandboxSummary = null,
  // Collaboration: "Happening now in shared projects" (collab/LiveNow), on both the full and the simple view.
  liveNow = null,
  // Chat was just turned on (Features › Chat, the first run): the cursor goes straight to the prompt.
  autoFocusPrompt = false,
}) => {
  const navigate = useNavigate();
  const [view, setView] = useState('welcome'); // welcome, projects, templates
  const [simpleChoice, setSimple] = useState(readSimpleView);
  // The simple view is Flow's own chat (three choices, then one question), so it exists only while Chat is on:
  // `chatOn` is the person's switch and the plan together, `chatAvailable` whether the plan has chat at all. Without it
  // the switch is off (disabled, or gone when the plan has no chat) and the person is pointed at an AI tool of their own.
  const [chatOn, , chatAvailable] = useChatFeature();
  // Sandbox-first with Chat on keeps the Simple view switch: the full view shows the sandbox under the chips, the
  // simple view stays just the three choices (the sandbox is one click away under Sandbox in the sidebar).
  const simple = chatOn && simpleChoice;
  // What the simple view's choices add in front of the prompt (e.g. "Create an artifact: "), so the agent knows what was asked.
  const simplePrefixRef = React.useRef('');
  const submittedByVoiceRef = React.useRef(false);
  // The simple view's first choice ('ask' | 'create' | 'build'), reported with the app it asks for.
  const simpleModeRef = React.useRef(null);
  // Something typed in the prompt and not sent: leaving Home (or the page) with it is a prompt abandoned.
  const draftTypedRef = React.useRef(false);
  useEffect(() => {
    let reported = false;
    const leftDraft = () => {
      if (reported || !draftTypedRef.current) { return; }
      reported = true;
      friction('prompt_abandoned', true);
    };
    window.addEventListener('pagehide', leftDraft);
    return () => { window.removeEventListener('pagehide', leftDraft); leftDraft(); };
  }, []);
  const toggleSimple = () => {
    if (!chatOn) { return; }
    const next = !simple;
    setSimple(next);
    try { localStorage.setItem(SIMPLE_VIEW_KEY, String(next)); } catch (_error) { /* not remembered */ }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [recentProjects, setRecentProjects] = useState([]);
  const [starredProjects, setStarredProjects] = useState([]);
  const [recentProjectsVisited, setRecentProjectsVisited] = useState([]);
  // Which of the sessions above currently have a real /run_sse in flight
  // (adk-be-app's own active_runs registry) — same data source/endpoint as
  // BackgroundTasksIndicator's "N tasks running" badge, polled the same
  // way, just rendered here as a per-row dot instead of a count.
  const [activeSessionIds, setActiveSessionIds] = useState([]);
  const [favoriteProjects, setFavoriteProjects] = useState([]);
  const [recentConsoleProjects, setRecentConsoleProjects] = useState([]);
  // First load of each Home list: skeletons sized like the real rows/cards
  // stand in until it settles, so nothing pops in and shifts the page, and
  // the empty texts ("No sessions yet.") never show while it's still loading.
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [favoritesLoading, setFavoritesLoading] = useState(true);
  const [recentProjectsLoading, setRecentProjectsLoading] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // A folder or zip dropped on the prompt: "Start a project from it" (New project › Import) rather than an attachment.
  const importOffer = useProjectImportOffer({ selectedTenant, onAttach: (files) => setAttachedFiles((prev) => [...prev, ...files]) });
  // Task #205: brings back a playbook selector on the Home omnibar (task #87
  // removed the old Coding/Chat/AI-Playbook mode selector, but that also
  // silently removed any way to skip Auto's server-side classification).
  // Reuses the exact same PlaybookPicker/PlaybooksService pair the chat
  // panel's own selector uses (see VfsChatPanel.jsx) rather than a parallel
  // component — 'auto' is the same sentinel, same default, same meaning:
  // "let the usual routing decide" (unchanged behavior when left on Auto).
  const [playbooks, setPlaybooks] = useState([]);
  const [homePlaybookChoice, setHomePlaybookChoice] = useState('auto');
  // Playbooks left out by the person's plan (docs/SANDBOX_FIRST.md): the picker stays on Auto (unknown = included).
  const planFeatures = usePlanFeatures();
  const playbookChoice = planFeatures.playbooks ? homePlaybookChoice : 'auto';
  useEffect(() => {
    let cancelled = false;
    listPlaybooks().then((list) => {
      if (!cancelled) setPlaybooks(list);
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('⚠️ Failed to load playbooks list:', error);
    });
    return () => { cancelled = true; };
  }, []);
  // The drifting hexagons behind the landing: two to start, one more every 5-10 seconds up to eight.
  const [hexagons, setHexagons] = useState(() => Array.from({length: 2}, (_, i) => ({
    id: i,
    size: Math.floor(Math.random() * 251) + 50,
    isNew: false,
  })));
  useEffect(() => {
    if (hexagons.length >= 8) return undefined;
    const timer = setTimeout(() => {
      setHexagons((prev) => (prev.length >= 8 ? prev : [...prev, {id: prev.length, size: Math.floor(Math.random() * 251) + 50, isNew: true}]));
    }, Math.random() * 5000 + 5000);
    return () => clearTimeout(timer);
  }, [hexagons.length]);
  const dragCounterRef = React.useRef(0);
  const fileInputRef = React.useRef(null);
  const omnibarRef = React.useRef(null);
  useEffect(() => {
    if (!autoFocusPrompt) return undefined;
    const timer = setTimeout(() => omnibarRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [autoFocusPrompt]);
  const handleOmnibarSubmitRef = React.useRef(null);
  // Voice, hands-free: one click, then say what you want — it is sent when you stop talking. The wave shows it is heard.
  const voice = useHandsFreePrompt((text) => {
    // The chat that opens next keeps listening: the conversation continues hands-free.
    offerVoiceHandoff();
    submittedByVoiceRef.current = true;
    handleOmnibarSubmitRef.current?.(text);
  });
  const voiceEnabled = import.meta.env.VITE_VOICE_ENABLED !== 'false';
  const voiceLabel = voice.phase === 'off' ? 'Talk hands-free' : 'Stop listening';
  const voiceStatus = {
    listening: 'Listening — say what you want to build',
    hearing: 'Hearing you…',
    transcribing: 'Understanding…'
  }[voice.phase];
  const voiceError = {
    failed: 'Could not turn what you said into text. Try again.',
    permission: 'The microphone is blocked. Allow it in the browser to speak.',
    unsupported: 'This browser cannot record audio.'
  }[voice.error];
  // Task #163: neither a mouse click nor (especially) the Shift+Enter/
  // Cmd+Enter keyboard shortcut gave any visible confirmation that a prompt
  // was actually sent — the omnibar just sat there while playbook
  // auto-selection + project creation ran in the background (a couple of
  // seconds, per handleStartChatFromHome). `sendPressed` drives a one-shot
  // tap animation on the Send button (cleared via a short timeout);
  // `isSubmittingPrompt` keeps it disabled with a spinner for as long as the
  // submit is actually in flight, as a fallback in case navigation doesn't
  // happen immediately (e.g. a "not build-related" prompt classification
  // that stays on Home instead of navigating away).
  const [sendPressed, setSendPressed] = useState(false);
  const [isSubmittingPrompt, setIsSubmittingPrompt] = useState(false);
  const sendPressedTimeoutRef = React.useRef(null);
  const submittingResetTimeoutRef = React.useRef(null);

  // Task #168 originally had Home embed the chat directly instead of a full
  // navigate() hand-off, to avoid a blank-page flash while Canvas mounted.
  // REMOVED per explicit instruction: the embedded view never reflected its
  // conversationId into the URL (Home stayed on the bare `/` route for the
  // whole embedded-chat phase), so a refresh/remount mid-conversation had
  // nothing to resume from and looked exactly like the chat had vanished —
  // live-confirmed via screenshot (an active, tool-call-populated
  // conversation sitting behind a bare `/` URL). A later attempt to patch
  // this by syncing the URL from the embedded state introduced its own
  // regression (redirect stopped firing, hero prompt vanished) — not worth
  // chasing further. Home now always hands off to the SAME proven path
  // App.jsx's handleStartChatFromHome already uses for every other entry
  // point (recent sessions, Catalog playbooks, the plain /chat door):
  // select the playbook, mint the session id, navigate straight to
  // `/chat?conversationId=<id>` (or `/playbook/:itemId` for a 'both'-mode
  // match) with the prompt riding in router state. The destination route
  // owns the real chat UI end to end — no separate embedded rendering path
  // to keep in sync with the URL.

  // Recent projects/conversations — the `projects` prop is always `[]` (a
  // stale leftover from the deleted BFF/MongoDB recent-projects collection,
  // task #52), so this never had real data to show. Sourced instead from
  // real ADK agent sessions (same data + navigation logic as Memories.jsx's
  // listAdkSessions/parseVfsSessionId/handleSelectConversation) — this
  // covers vfs-only vfs projects created purely via chat, which never
  // appear in any Console/Catalog project list.
  const userId = user?.sub || user?.id || user?.email;
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setRecentProjectsVisited([]);
      setSessionsLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const sessions = await listAdkSessions(userId);
        const sorted = [...sessions].sort((a, b) => (b.lastUpdateTime || 0) - (a.lastUpdateTime || 0));
        const recentSessions = sorted.slice(0, MAX_RECENT_PROJECTS).map((session) => {
          const parsed = parseVfsSessionId(session.id);
          return {
            id: session.id,
            sessionId: session.id,
            projectId: parsed?.projectId || null,
            name: session.name || 'Untitled conversation',
            updatedAt: session.lastUpdateTime ? new Date(session.lastUpdateTime * 1000).toISOString() : null,
          };
        });
        if (!cancelled) {
          setRecentProjectsVisited(recentSessions);
        }

        // Fetch thumbnails in the background for sessions tied to a real vfs
        // project — chat-only sessions (parsed.projectId null) have none.
        const tenantId = selectedTenant?.tenantId || selectedTenant?.id ||
          miaFlowAuth.getSelectedTenant()?.tenantId || miaFlowAuth.getSelectedTenant()?.id;
        recentSessions.forEach(async (session) => {
          if (!session.projectId || !tenantId) { return; }
          try {
            const thumbnail = await getProjectThumbnail(session.projectId, tenantId);
            if (thumbnail && !cancelled) {
              setRecentProjectsVisited((prev) =>
                prev.map((p) => (p.id === session.id ? { ...p, thumbnail } : p)));
            }
          } catch (error) {
            console.debug(`Failed to fetch thumbnail for session ${session.id}:`, error);
          }
        });
      } catch (error) {
        if (!cancelled) {
          setRecentProjectsVisited([]);
        }
        console.debug('Failed to load recent ADK sessions for Home:', error);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, selectedTenant]);

  // Live "is this session running right now" dot for the Recent Sessions
  // list — same /active endpoint + polling cadence as
  // BackgroundTasksIndicator's own "N tasks running" badge (which sits
  // fixed in the corner regardless of page), just surfaced per-row here
  // instead. A plain interval, not that component's own poll: Home is a
  // single page-scoped view, no need for the badge's cross-page portal/
  // open-panel machinery just to blink one dot.
  useEffect(() => {
    if (!userId) {
      setActiveSessionIds([]);
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const ids = await listActiveAdkSessions(userId);
        if (!cancelled) { setActiveSessionIds(ids); }
      } catch {
        // Best-effort UI affordance — a transient failure just skips this
        // tick, not worth surfacing to the user.
      }
    };
    poll();
    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId]);

  // Favorite projects — real ones (from miaPlatformDataService's
  // favorites API, same source as Projects.jsx), not the dead
  // `projects.filter(p => p.starred)` below (starredProjects/`starred`
  // flag never had real data either, same always-empty-`projects`-prop
  // issue as recentProjectsVisited above). getFavorites only returns
  // project IDs, so the real project objects (name/thumbnail/etc.) come
  // from getProjects and are filtered down to just the favorited ids —
  // same two-step fetch Projects.jsx would need if it also lacked a
  // get-project-by-id endpoint.
  useEffect(() => {
    let cancelled = false;
    const tenantId = selectedTenant?.tenantId || selectedTenant?.id ||
      miaFlowAuth.getSelectedTenant()?.tenantId || miaFlowAuth.getSelectedTenant()?.id;
    if (!tenantId) {
      setFavoriteProjects([]);
      setFavoritesLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const favoriteIds = await miaPlatformDataService.getFavorites(tenantId);
        if (!favoriteIds || favoriteIds.length === 0) {
          if (!cancelled) setFavoriteProjects([]);
          return;
        }
        const idSet = new Set(favoriteIds);

        // BUG FIX: this used to fetch a single hardcoded page=1/per_page=50
        // and intersect with the favorite-id set, same real bug Projects.jsx
        // was already fixed for (see its own fetchProjects comment) — once a
        // tenant has more than 50 projects, a real favorited project beyond
        // that first page silently never matched and just disappeared from
        // this widget, live-confirmed against a tenant with >50 projects
        // where several genuinely-starred projects (visible correctly on
        // /projects) were missing here entirely. Loop pages the same way,
        // capped the same way, until every favorite id has a chance to match.
        const perPage = 100;
        const MAX_PAGES = 50; // 5000 projects — far beyond any real tenant
        let projectsData = [];
        let page = 1;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const result = await miaPlatformDataService.getProjects(tenantId, null, page, perPage);
          projectsData = projectsData.concat(result.data || []);
          if (!result.pagination?.has_more || page >= MAX_PAGES) break;
          page += 1;
        }
        const matched = projectsData.filter((p) => idSet.has(p._id));
        // Most-recent-first, same precedence recentConsoleProjects/
        // sortedProjects already use elsewhere in this file — otherwise
        // this just showed favorites in whatever order the registry
        // happened to return them (not recency), and `.slice(0, 3)` below
        // could easily show 3 old favorites while a just-favorited project
        // sat off-screen.
        matched.sort((a, b) => {
          const dateA = lastActivityMs(a);
          const dateB = lastActivityMs(b);
          return dateB - dateA;
        });
        if (cancelled) return;
        setFavoriteProjects(matched);

        // Fetch thumbnails in the background, same pattern as recentProjectsVisited above.
        matched.forEach(async (project) => {
          try {
            const thumbnail = await getProjectThumbnail(project._id, tenantId);
            if (thumbnail && !cancelled) {
              setFavoriteProjects((prev) =>
                prev.map((p) => (p._id === project._id ? { ...p, thumbnail } : p)));
            }
          } catch (error) {
            console.debug(`Failed to fetch thumbnail for favorite project ${project._id}:`, error);
          }
        });

        // BUG FIX: a vfs-tier favorited project's `name` here is whatever
        // the BFF's registry backfilled it with — for a project the agent
        // created directly (never named via the FE), that's the raw
        // internal projectId used verbatim as a placeholder name (see
        // project-store.js's backfillFromVfs), e.g. "helloapp130129862"
        // instead of a real title. Projects.jsx already corrects this by
        // reading the project's live `.mia/project.json` (the agent's real,
        // current name/description/technologies) — same fetch, same
        // best-effort/silently-skip-on-failure pattern, so this widget
        // never shows a raw project id as its display name again.
        matched.forEach(async (project) => {
          try {
            const metadata = await readProjectMetadata(project._id);
            if (metadata && (metadata.name || metadata.description) && !cancelled) {
              setFavoriteProjects((prev) =>
                prev.map((p) => (p._id === project._id
                  ? { ...p, name: metadata.name || p.name, description: metadata.description || p.description }
                  : p)));
            }
          } catch (error) {
            // Silently ignore — missing/unreadable metadata just keeps whatever the list endpoint already returned.
          }
        });
      } catch (error) {
        if (!cancelled) setFavoriteProjects([]);
        console.debug('Failed to load favorite projects for Home:', error);
      } finally {
        if (!cancelled) setFavoritesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTenant]);

  const handleSelectFavoriteProject = (project) => {
    const tenantId = selectedTenant?.tenantId || selectedTenant?.id || project.tenantId;
    const projectId = project._id;
    if (!tenantId || !projectId) return;
    sessionStorage.setItem('mia_switching_to_project', projectId);
    navigate(projectHomeUrl(tenantId, projectId));
  };

  // Real recent PROJECTS (distinct from recentProjectsVisited above, which
  // is recent ADK chat SESSIONS) — same data source/sort Projects.jsx uses
  // (getProjects sorted by visitedAt/updatedAt/createdAt), just the top 3.
  useEffect(() => {
    let cancelled = false;
    const tenantId = selectedTenant?.tenantId || selectedTenant?.id ||
      miaFlowAuth.getSelectedTenant()?.tenantId || miaFlowAuth.getSelectedTenant()?.id;
    if (!tenantId) {
      setRecentConsoleProjects([]);
      setRecentProjectsLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const result = await miaPlatformDataService.getProjects(tenantId, null, 1, 20);
        const sorted = [...(result?.data || [])].sort((a, b) => {
          const dateA = lastActivityMs(a);
          const dateB = lastActivityMs(b);
          return dateB - dateA;
        });
        const top3 = sorted.slice(0, 3);
        if (!cancelled) setRecentConsoleProjects(top3);
        top3.forEach(async (project) => {
          try {
            const thumbnail = await getProjectThumbnail(project._id, tenantId);
            if (thumbnail && !cancelled) {
              setRecentConsoleProjects((prev) =>
                prev.map((p) => (p._id === project._id ? { ...p, thumbnail } : p)));
            }
          } catch (error) {
            console.debug(`Failed to fetch thumbnail for recent project ${project._id}:`, error);
          }
        });
      } catch (error) {
        if (!cancelled) setRecentConsoleProjects([]);
        console.debug('Failed to load recent console projects for Home:', error);
      } finally {
        if (!cancelled) setRecentProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTenant]);

  // A deleted project leaves Recent at once (the lists above load once on mount).
  useEffect(() => {
    const onDeleted = (event) => {
      const id = event?.detail?.projectId;
      if (!id) { return; }
      setRecentConsoleProjects((prev) => prev.filter((p) => p._id !== id));
      setRecentProjectsVisited((prev) => prev.filter((p) => p.projectId !== id));
    };
    window.addEventListener('flow:project-deleted', onDeleted);
    return () => window.removeEventListener('flow:project-deleted', onDeleted);
  }, []);

  const handleSelectRecentConsoleProject = (project) => {
    const tenantId = selectedTenant?.tenantId || selectedTenant?.id || project.tenantId;
    const projectId = project._id;
    if (!tenantId || !projectId) return;
    sessionStorage.setItem('mia_switching_to_project', projectId);
    navigate(projectHomeUrl(tenantId, projectId));
  };

  useEffect(() => {
    // Load recent and starred projects from props
    const sortedProjects = [...projects].sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA;
    });
    setRecentProjects(sortedProjects.slice(0, 6));
    
    const starred = projects.filter(p => p.starred);
    setStarredProjects(starred);
    
    // Fetch thumbnails for all projects in the background
    const allProjects = [...sortedProjects.slice(0, 6), ...starred];
    allProjects.forEach(async (project) => {
      try {
        const tenantId = project.tenant?.tenantId || project.tenantId;
        const thumbnail = await getProjectThumbnail(project._id || project.projectId, tenantId);
        if (thumbnail) {
          // Update both recentProjects and starredProjects
          setRecentProjects(prevProjects => 
            prevProjects.map(p => 
              (p._id === project._id || p.projectId === project._id) ? { ...p, thumbnail } : p
            )
          );
          setStarredProjects(prevProjects => 
            prevProjects.map(p => 
              (p._id === project._id || p.projectId === project._id) ? { ...p, thumbnail } : p
            )
          );
        }
      } catch (error) {
        // Silently ignore thumbnail fetch errors
        console.debug(`Failed to fetch thumbnail for project ${project._id}:`, error);
      }
    });
  }, [projects]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           (selectedCategory === 'starred' && project.starred) ||
                           (selectedCategory === 'recent' && recentProjects.includes(project));
    return matchesSearch && matchesCategory;
  });

  const filteredTemplates = templates.filter(template => {
    return template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           template.description?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Static placeholder — task #87 removes the Coding/Chat/AI Playbook mode
  // selector from the Home hero. There's nothing left to pick: every submit
  // goes through the same 'agent' auto-routing (see handleOmnibarSubmit),
  // which auto-selects the right AI Playbook or falls back to just letting
  // the conversation happen naturally, so the placeholder no longer needs
  // to describe a mode.
  const getPlaceholderText = () => 'Ask a question, or describe what you want to build...  (Shift+Enter for new line)';

  // Handle omnibar submit — always routes through 'agent' mode (task #87).
  // 'agent' already does the smart routing that used to require an upfront
  // mode choice: it calls the BFF's playbook auto-selector
  // (mia-flow-bff/lib/playbook-selection.js via
  // miaPlatformDataService.selectPlaybook) to see if a specific AI Playbook
  // fits the prompt, and if nothing confidently matches, defaults to the
  // normal project-backed chat rather than asking the user to choose
  // Coding vs. Chat — see handleStartChatFromHome's 'agent' branch in
  // App.jsx for the full behavior.
  // Same routing rule as Memories.jsx's handleSelectConversation: a session
  // tied to a real vfs project (parseVfsSessionId returned a projectId)
  // lands on Code so the result is visible; a chat-only session (no code
  // produced) lands on plain Chat instead of bare /code, which would
  // otherwise silently auto-create an unwanted new project.
  const handleSelectRecentSession = async (session) => {
    const tenantId = selectedTenant?.tenantId || selectedTenant?.id ||
      miaFlowAuth.getSelectedTenant()?.tenantId || miaFlowAuth.getSelectedTenant()?.id;
    let projectId = session.projectId;
    // `session.projectId` only ever comes from parseVfsSessionId, which can
    // only decode the OLD deterministic `vfs-{projectId}` session id shape —
    // a real ADK-issued session id (a plain UUID, the normal case for any
    // conversation that started on the plain /chat door) never matches that
    // pattern, so this was always null for the vast majority of real
    // sessions even when they DID build a real project. Fall back to the
    // conversationId -> projectId reverse lookup (same one VfsChatPanel uses
    // to resume a /chat conversation's Code button) before giving up and
    // treating this as a chat-only session.
    if (!projectId && tenantId) {
      projectId = await findProjectByConversationId(session.sessionId);
    }
    if (projectId && tenantId) {
      navigate(`/code/${tenantId}/${projectId}?conversationId=${encodeURIComponent(session.sessionId)}`);
    } else {
      navigate(`/chat?conversationId=${encodeURIComponent(session.sessionId)}`);
    }
  };

  const handleOmnibarSubmit = async (value) => {
    if (!value.trim() || isSubmittingPrompt) return;

    // BUG FIX (live-reported, screen-recorded): this textarea is
    // uncontrolled and was NEVER cleared after submit — only the
    // `isSubmittingPrompt` overlay below hid it. On any path that takes a
    // while to actually navigate away (the 'agent' route awaits a real
    // network call, `selectPlaybook`, before `navigate()`) or never
    // navigates at all (a rejected call, or the 6s failsafe below firing
    // first), the overlay drops and reveals this same stale prompt text
    // still sitting in the box — reading as "Home showed my prompt again".
    // Clearing the actual DOM value here, synchronously and unconditionally,
    // means there's nothing stale left to reveal regardless of which path
    // handleStartChat takes below.
    const omnibarInput = omnibarRef.current;
    draftTypedRef.current = false;
    if (omnibarInput) {
        omnibarInput.value = '';
        omnibarInput.style.height = 'auto';
    }

    // Tap animation — same code path for a mouse click and the Shift+Enter/
    // Cmd+Enter keyboard shortcut, so both get identical feedback.
    setSendPressed(true);
    if (sendPressedTimeoutRef.current) clearTimeout(sendPressedTimeoutRef.current);
    sendPressedTimeoutRef.current = setTimeout(() => setSendPressed(false), 350);

    // Disabled+spinner for the duration of the actual submit — handleStartChat
    // below either navigates away (unmounting Home) or shows an error toast
    // (no tenant resolved yet); this timeout is only a safety net so the
    // button never gets stuck disabled if neither happens.
    // BUG FIX (live-reported, screen-recorded): 6s was tuned as "long enough
    // to never matter", but handleStartChatFromHome's real path (App.jsx)
    // awaits a live network call — selectPlaybook — before navigating, and
    // that call plus the navigation/mount that follows can legitimately run
    // past 6s. When it did, this fired first: the overlay dropped and
    // revealed the stale hero text/omnibar underneath for a moment BEFORE
    // the eventual navigate() away, reading as a broken "reset" mid-
    // transition. Since navigation unmounts Home outright on success (the
    // only path that matters), this is purely a stuck-button safety net —
    // widened well past any real classification call so it only ever fires
    // for a genuinely hung/broken request, not a merely-slow one.
    setIsSubmittingPrompt(true);
    if (submittingResetTimeoutRef.current) clearTimeout(submittingResetTimeoutRef.current);
    submittingResetTimeoutRef.current = setTimeout(() => setIsSubmittingPrompt(false), 20000);

    // Always hand off to the same path every other entry point already uses
    // (recent sessions, Catalog playbooks, the plain /chat door):
    // handleStartChatFromHome in App.jsx selects the playbook, mints the
    // session id, and navigates straight to `/chat?conversationId=<id>` (or
    // `/playbook/:itemId` for a 'both'-mode match) with the prompt in router
    // state. See the removed-embedded-chat comment above `initialLocationKeyRef`
    // for why this used to be handled locally instead.
    const prefix = simple ? simplePrefixRef.current : '';
    const prompt = prefix && !value.toLowerCase().startsWith(prefix.trim().toLowerCase()) ? `${prefix}${value}` : value;
    const viaVoice = submittedByVoiceRef.current;
    submittedByVoiceRef.current = false;
    appRequested({
      mode: simple ? simpleModeRef.current || 'simple' : 'full',
      playbook: playbookChoice === 'auto' ? 'none' : playbookChoice,
      hasFiles: attachedFiles.length > 0,
      viaVoice,
    });
    handleStartChat(prompt, 'agent', attachedFiles, null, { playbookName: playbookChoice });
    setAttachedFiles([]);
  };
  handleOmnibarSubmitRef.current = handleOmnibarSubmit;

  useEffect(() => {
    return () => {
      if (sendPressedTimeoutRef.current) clearTimeout(sendPressedTimeoutRef.current);
      if (submittingResetTimeoutRef.current) clearTimeout(submittingResetTimeoutRef.current);
    };
  }, []);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...selectedFiles]);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    if (importOffer.takeDrop(e.dataTransfer)) { return; }
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleStartChat = (prompt = '', mode = 'code', files = [], fullStackTemplate = null, playbookOptions = {}) => {
    if (onStartChat) {
      onStartChat(prompt, mode, files, fullStackTemplate, playbookOptions);
    }
  };

  // Starter chips: the first group fills the prompt box with the start of a request (the user finishes it); the second
  // offers whole ideas. "Analyze a document" also opens the file picker, since a document is what it needs.
  const quickActions = [
    {key: 'artifact', label: 'Create an artifact', icon: 'sparkles', prompt: 'Create an artifact: '},
    {key: 'new', label: 'New project', icon: 'plus', prompt: 'Create a new project: '},
    {key: 'bug', label: 'Fix a bug', icon: 'tools', prompt: 'Fix this bug: '},
    {key: 'refactor', label: 'Refactor code', icon: 'refresh', prompt: 'Refactor this code: '},
    {key: 'document', label: 'Analyze a document', icon: 'file', prompt: 'Analyze this document: ', attach: true},
  ];
  const ideas = ['Turn a PDF into a report'];
  const firstName = user?.name?.split(' ')[0];
  const continueSession = recentProjectsVisited[0] || null;
  const favoriteIds = new Set(favoriteProjects.map((project) => project._id));

  const fillPrompt = (text, {attach = false} = {}) => {
    const input = omnibarRef.current;
    if (input) {
      input.value = text;
      input.focus();
      input.setSelectionRange(text.length, text.length);
      input.style.height = 'auto';
      input.style.height = `${Math.min(input.scrollHeight, 240)}px`;
    }
    if (attach) { fileInputRef.current?.click(); }
  };

  // A stable gradient per project, for the card header when it has no preview image yet.
  const GRADIENTS = [
    'linear-gradient(135deg, #f2b774, #e8793a)',
    'linear-gradient(135deg, #334155, #111827)',
    'linear-gradient(135deg, #c7d2e5, #d9dfeb)',
    'linear-gradient(135deg, #7c9cf5, #3b63e6)',
    'linear-gradient(135deg, #8bd3c1, #3a9d8a)',
  ];
  const gradientFor = (name) => {
    const text = String(name || '');
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) { hash = (hash * 31 + text.charCodeAt(i)) % 997; }
    return GRADIENTS[hash % GRADIENTS.length];
  };

  // The one prompt box (attach, hands-free voice, playbook, send), shared by the full home and the simple view.
  const renderPromptBox = ({placeholder, label}) => (
      <div
        className={`hm-prompt${isDraggingOver ? ' hm-prompt-over' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown,text/csv,application/json"
          style={{display: 'none'}}
          onChange={handleFileInputChange}
        />
        {isDraggingOver && <div className="hm-prompt-drop"><Icon name="upload" size={28} /><span>Drop files to attach, or a folder or zip to start a project</span></div>}
        {importOffer.element}
        {isSubmittingPrompt && (
          <div className="hm-prompt-busy">
            <span className="hm-spinner" aria-hidden="true" />
            <span>Getting things ready…</span>
          </div>
        )}
        <textarea
          aria-label={label}
          className="hm-input"
          placeholder={placeholder}
          ref={omnibarRef}
          rows={2}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter adds a line (as in the chat itself).
            if (e.key === 'Enter' && !e.shiftKey && e.target.value.trim()) {
              e.preventDefault();
              handleOmnibarSubmit(e.target.value);
            }
          }}
          onInput={(e) => {
            draftTypedRef.current = Boolean(e.target.value.trim());
            const maxHeight = 240;
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, maxHeight)}px`;
            e.target.style.overflowY = e.target.scrollHeight > maxHeight ? 'auto' : 'hidden';
          }}
        />
        {attachedFiles.length > 0 && (
          <div className="hm-files">
            {attachedFiles.map((file, index) => (
              <span className="hm-file" key={`${file.name}-${index}`}>
                <Icon name="file" size={13} />
                <span className="hm-file-name" title={file.name}>{file.name}</span>
                <button aria-label={`Remove ${file.name}`} className="hm-file-remove" onClick={() => handleRemoveFile(index)} type="button">
                  <Icon name="x" size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        {voiceError && <div className="hm-voice-error" role="alert">{voiceError}</div>}
        {voiceStatus && (
          <div aria-live="polite" className="hm-voice-status" role="status">
            <VoiceWave level={voice.level} mode={voice.phase === 'hearing' ? 'live' : 'idle'} />
            <span>{voiceStatus}</span>
          </div>
        )}
        <div className="hm-prompt-bar">
          <div className="hm-prompt-left">
            <button className="hm-attach" onClick={() => fileInputRef.current?.click()} title="Attach files" type="button">
              <Icon name="file" size={16} />
            </button>
            {voiceEnabled && (
              <button
                aria-label={voiceLabel}
                aria-pressed={voice.phase !== 'off'}
                className={`hm-attach hm-mic${voice.phase !== 'off' ? ' hm-mic-recording' : ''}`}
                onClick={voice.toggle}
                title={voiceLabel}
                type="button"
              >
                <HeadsetIcon />
              </button>
            )}
            <PlaybookPicker
              disabled={!planFeatures.playbooks}
              disabledReason={`Choosing an assistant isn’t included in ${planLabel(planFeatures.planName)}. The default one answers.`}
              playbooks={playbooks}
              selected={playbookChoice}
              onSelect={setHomePlaybookChoice}
            />
          </div>
          <button
            aria-label="Send"
            className={`hm-send${sendPressed ? ' hm-send-pressed' : ''}`}
            disabled={isSubmittingPrompt}
            onClick={() => {
              const input = omnibarRef.current;
              if (input?.value.trim()) { handleOmnibarSubmit(input.value); }
            }}
            title={isSubmittingPrompt ? 'Sending…' : 'Send (Enter)'}
            type="button"
          >
            {isSubmittingPrompt ? <span className="hm-spinner hm-spinner-light" aria-hidden="true" /> : <ArrowIcon size={20} />}
          </button>
        </div>
      </div>
  );

  const renderWelcomeView = () => (
    <div className="hm">
      <header className="hm-head">
        <div className="hm-head-text">
          <h1 className="hm-title">{firstName ? `Ready to flow, ${firstName}?` : 'Ready to flow?'}</h1>
          <p className="hm-subtitle">Build from scratch, fix a bug or modernize a legacy app.</p>
        </div>
        {sessionsLoading && !continueSession && <ContinueSkeleton />}
        {continueSession && (
          <div className="hm-continue">
            <span className="hm-continue-icon" aria-hidden="true"><Icon name="refresh" size={20} /></span>
            <div className="hm-continue-text">
              <strong>Continue</strong>
              <span>{continueSession.name} · {formatRelativeTime(continueSession.updatedAt)}</span>
            </div>
            <button className="hm-resume" onClick={() => handleSelectRecentSession(continueSession)} type="button">
              Resume <ArrowIcon size={16} />
            </button>
          </div>
        )}
      </header>

      {renderPromptBox({placeholder: 'Describe a bug to fix, an app to build, or drop a file...', label: 'What do you want to do?'})}

      <div className="hm-chips">
        {quickActions.map((action) => (
          <button className="hm-chip" key={action.key} onClick={() => fillPrompt(action.prompt, {attach: action.attach})} type="button">
            <Icon name={action.icon} size={14} /> {action.label}
          </button>
        ))}
        {ideas.map((idea) => (
          <button className="hm-chip hm-chip-idea" key={idea} onClick={() => fillPrompt(idea)} type="button">{idea}</button>
        ))}
      </div>

      {liveNow}
      {sandboxSummary}

      {(recentProjectsLoading || recentConsoleProjects.length > 0) && (
        <section>
          <div className="hm-sec-head">
            <h2>Recent projects</h2>
            <button className="hm-see-all" onClick={() => onNavigate && onNavigate('/projects')} type="button">See all</button>
          </div>
          {recentProjectsLoading ? (
            <div aria-busy="true" aria-label="Loading recent projects" className="hm-projects">
              {[0, 1, 2].map((i) => <ProjectCardSkeleton key={i} />)}
            </div>
          ) : (
          <div className="hm-projects">
            {recentConsoleProjects.map((project) => (
              <button className="hm-project" key={project._id} onClick={() => handleSelectRecentConsoleProject(project)} type="button">
                <span
                  className="hm-project-thumb"
                  style={project.thumbnail ? {backgroundImage: `url(${project.thumbnail})`} : {background: gradientFor(project.name)}}
                >
                  {favoriteIds.has(project._id) && <span className="hm-star" aria-label="Favorite">★</span>}
                </span>
                <span className="hm-project-info">
                  <strong>{project.name || 'Untitled project'}</strong>
                  <span>{formatRelativeTime(lastActivityAt(project))}</span>
                </span>
              </button>
            ))}
          </div>
          )}
        </section>
      )}

      {(sessionsLoading || favoritesLoading || recentProjectsVisited.length > 0 || favoriteProjects.length > 0) && (
        <div className="hm-lists">
          <div className="hm-list-card">
            <h2>Recent sessions</h2>
            {sessionsLoading ? <ListRowsSkeleton label="Loading recent sessions" /> : recentProjectsVisited.length > 0 ? recentProjectsVisited.slice(0, 3).map((session) => (
              <button className="hm-row" key={session.id} onClick={() => handleSelectRecentSession(session)} type="button">
                <span className="hm-row-name">
                  {session.name || 'Untitled conversation'}
                  {activeSessionIds.includes(session.id) && <span className="hm-live" title="Running right now" />}
                </span>
                <span className="hm-row-time">{formatRelativeTime(session.updatedAt)}</span>
              </button>
            )) : <p className="hm-empty">No sessions yet.</p>}
          </div>
          <div className="hm-list-card">
            <h2>Favorites</h2>
            {favoritesLoading ? <ListRowsSkeleton label="Loading favorites" /> : favoriteProjects.length > 0 ? favoriteProjects.slice(0, 3).map((project) => (
              <button className="hm-row" key={project._id} onClick={() => handleSelectFavoriteProject(project)} type="button">
                <span className="hm-row-name">{project.name || 'Untitled project'}</span>
                <span className="hm-row-time">{formatRelativeTime(lastActivityAt(project))}</span>
              </button>
            )) : <p className="hm-empty">Star a project to keep it here.</p>}
          </div>
        </div>
      )}

      <footer className="home-footer">
        <p>Made by Mia-Platform with ❤️ :-)</p>
      </footer>
    </div>
  );

  const renderSimpleView = () => (
    <HomeSimple
      continueSession={continueSession}
      firstName={firstName}
      formatRelativeTime={formatRelativeTime}
      liveNow={liveNow}
      onAttach={() => fileInputRef.current?.click()}
      onFocusPrompt={() => omnibarRef.current?.focus()}
      onMode={(mode) => { simpleModeRef.current = mode; }}
      onPrefix={(prefix) => { simplePrefixRef.current = prefix; }}
      onResume={() => handleSelectRecentSession(continueSession)}
      onShowFull={toggleSimple}
      promptBox={renderPromptBox}
      submitting={isSubmittingPrompt}
      voice={voice}
      voiceEnabled={voiceEnabled}
      voiceError={voiceError}
      voiceStatus={voiceStatus}
    />
  );

  const renderProjectsView = () => (
    <div className="home-projects-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('welcome')}>
          <Icon name="arrow-left" size={16} />
          Back
        </button>
        <h1 className="view-title">Projects</h1>
      </div>

      {/* Search and Filters */}
      <div className="view-controls">
        <div className="search-bar">
          <Icon name="search" className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All projects
          </button>
          <button 
            className={`filter-tab ${selectedCategory === 'recent' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('recent')}
          >
            Recent
          </button>
          <button 
            className={`filter-tab ${selectedCategory === 'starred' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('starred')}
          >
            <Icon name="star" size={14} /> Starred
          </button>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="projects-grid large">
        {filteredProjects.length === 0 ? (
          <div className="empty-state">
            <Icon name="folder" size={48} className="empty-icon" />
            <h3>No projects found</h3>
            <p>Try adjusting your search or create a new project</p>
            <button className="primary-btn" onClick={() => setView('welcome')}>
              Create Project
            </button>
          </div>
        ) : (
          filteredProjects.map((project, index) => (
            <ProjectCard
              key={project._id || project.projectId || project.id || `project-${index}`}
              project={project}
              onSelect={() => onSelectProject && onSelectProject(project)}
              large
            />
          ))
        )}
      </div>
    </div>
  );

  const renderTemplatesView = () => (
    <div className="home-templates-view">
      <div className="view-header">
        <button className="back-btn" onClick={() => setView('welcome')}>
          <Icon name="arrow-left" size={16} />
          Back
        </button>
        <h1 className="view-title">Templates</h1>
      </div>

      {/* Search */}
      <div className="view-controls">
        <div className="search-bar">
          <Icon name="search" className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Templates Grid */}
      <div className="templates-grid large">
        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <Icon name="template" size={48} className="empty-icon" />
            <h3>No templates found</h3>
            <p>Try adjusting your search</p>
          </div>
        ) : (
          filteredTemplates.map((template, idx) => (
            <TemplateCard
              key={idx}
              template={template}
              onSelect={() => onSelectTemplate && onSelectTemplate(template)}
              large
            />
          ))
        )}
      </div>
    </div>
  );

  // A plan without Flow's chat: nothing to ask here, so Home leads to the setup of an AI tool of their own (Claude, ChatGPT,
  // Cursor... working in their Flow sandbox), the same steps as the first sign-in (/welcome).
  const renderExternalChatOnboarding = () => (
    <div className="hm">
      <header className="hm-head">
        <div className="hm-head-text">
          <h1 className="hm-title">{firstName ? `Welcome, ${firstName}` : 'Welcome'}</h1>
          <p className="hm-subtitle">Your plan builds with the AI tool you already use, in your own Flow sandbox: connect it once.</p>
        </div>
      </header>
      <section aria-labelledby="hm-external-chat-title" className="hm-external-chat">
        <h2 id="hm-external-chat-title">Connect your AI tool</h2>
        <p>Claude, ChatGPT, Cursor, Copilot and more can create, preview and ship apps in your sandbox. It takes about a minute.</p>
        <button className="hm-chip hm-external-chat-cta" onClick={() => onNavigate && onNavigate('/welcome')} type="button">Set up your AI tool</button>
      </section>
    </div>
  );

  return (
    <div className={`home-container${fadeOut ? ' home-fading-out' : ''}`}>
      {view === 'welcome' && chatAvailable && (
        <button
          aria-checked={simple}
          aria-label="Simple view"
          className="hs-toggle"
          disabled={!chatOn}
          onClick={toggleSimple}
          role="switch"
          title={chatOn ? 'Simple view' : 'The simple view needs Chat: turn it on first'}
          type="button"
        >
          <span aria-hidden="true" className={`hs-switch${simple ? ' hs-switch-on' : ''}`}><span className="hs-knob" /></span>
        </button>
      )}
      {view === 'welcome' && (
        <div className="home-hexagon-container" aria-hidden="true">
          {hexagons.map((hex, i) => (
            <div
              className={`home-hexagon home-hexagon-${(i % 8) + 1}${hex.isNew ? ' hexagon-spawn' : ''}`}
              key={hex.id}
              style={{width: `${hex.size}px`, height: `${hex.size}px`}}
            >
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <polygon points="50 0, 93.301 25, 93.301 75, 50 100, 6.699 75, 6.699 25" />
              </svg>
            </div>
          ))}
        </div>
      )}
      {view === 'welcome' && (!chatAvailable ? renderExternalChatOnboarding() : simple ? renderSimpleView() : renderWelcomeView())}
      {view === 'projects' && renderProjectsView()}
      {view === 'templates' && renderTemplatesView()}
    </div>
  );
};

// Template Card Component
const TemplateCard = ({template, onSelect, large = false}) => {
  return (
    <button 
      className={`template-card ${large ? 'large' : ''}`}
      onClick={onSelect}
    >
      <div className="template-preview">
        {template.preview ? (
          <img src={template.preview} alt={template.name} />
        ) : (
          <div className="template-preview-placeholder">
            <Icon name="template" size={32} />
          </div>
        )}
      </div>
      <div className="template-info">
        <h3 className="template-name">{template.name}</h3>
        <p className="template-description">{template.description}</p>
        {template.tags && (
          <div className="template-tags">
            {template.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="template-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

TemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  large: PropTypes.bool,
};

Home.propTypes = {
  onStartChat: PropTypes.func,
  onSelectProject: PropTypes.func,
  onSelectTemplate: PropTypes.func,
  onNavigate: PropTypes.func,
  projects: PropTypes.array,
  templates: PropTypes.array,
  user: PropTypes.object,
  selectedTenant: PropTypes.object,
  hasGitConnection: PropTypes.bool,
  fadeOut: PropTypes.bool,
  sandboxSummary: PropTypes.node,
  liveNow: PropTypes.node,
  autoFocusPrompt: PropTypes.bool,
};

export default Home;
export { activateCodeLayout };
